import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoStudents } from "@/data/demoData";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

export type LeaderboardEntry = {
  rank: number;
  student_id: number;
  student_name: string;
  email: string;
  total_hours: number;
  tips_received: number;
  badges_count: number;
  avatar_url?: string;
};

export function useLeaderboard(period: "all" | "week" | "month" = "all") {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["admin-leaderboard", period],
    queryFn: async () => {
      if (user?.isDemo) {
        // Demo fallback: derive from mock student data
        const students = demoStudents as any[];
        return students.map((s, i) => ({
          rank: i + 1,
          student_id: s.id ?? i + 1,
          student_name: s.name ?? s.full_name ?? `Student ${i + 1}`,
          email: s.email ?? "",
          total_hours: s.total_hours ?? 0,
          tips_received: s.tips_received ?? 0,
          badges_count: s.badges_count ?? 0,
          avatar_url: s.avatar_url ?? undefined,
        })) as LeaderboardEntry[];
      }
      try {
        const res = await apiClient.get("/admin/leaderboard/hours", {
          params: { period },
        });
        return unwrapApiArray<any>(res).map((entry, index) => ({
          rank: Number(entry.rank || index + 1),
          student_id: Number(entry.student_id || entry.id || index + 1),
          student_name: entry.student_name || entry.name || "Unnamed student",
          email: entry.email || "",
          total_hours: Number(entry.total_hours || entry.hours || 0),
          tips_received: Number(entry.tips_received || entry.total_tips || entry.tip_count || 0),
          badges_count: Number(entry.badges_count || 0),
          avatar_url: entry.avatar_url,
        })) as LeaderboardEntry[];
      } catch (err) {
        console.error("Failed to fetch leaderboard:", err);
        return [] as LeaderboardEntry[];
      }
    },
  });
}

export function useStudentRank(studentId?: number) {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-rank", studentId],
    queryFn: async () => {
      if (!studentId) return null;
      if (user?.isDemo) {
        // Demo: return mock rank
        return { rank: 1, total: demoStudents.length } as StudentRank;
      }
      try {
        const res = await apiClient.get(`/admin/leaderboard/student/${studentId}/rank`);
        return unwrapApiObject<StudentRank>(res, null as any);
      } catch (err) {
        console.error("Failed to fetch student rank:", err);
        return null;
      }
    },
    enabled: !!studentId,
  });
}

export type StudentRank = {
  rank: number;
  total: number;
};
